import { useContext, useMemo } from 'react'
import Chip from '@mui/material/Chip'
import makeStyles from '@mui/styles/makeStyles'
import groupBy from 'lodash/groupBy'
import { Menu, Item, Separator, Submenu } from 'react-contexify'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { useSelector } from 'react-redux'
import { useDatasetDataContext } from '@datasets/dataset-details/datasetData/context'
import { useDatasetQueryContext } from '@datasets/dataset-query-context/hooks'
import { QueryOptionFilter } from '@datasets/dataset-query-context/utils/queryOptions'
import { comparisonOperatorConfigsByColType } from '@global/constants'
import { getColumnDataType } from './columnFilterPopover/utils'
import { ReadGridContextMenuContext } from './context/context-menu-context'
import { rowPropertyAliases } from './readGridToolbar/components/toggleColumns/config'
import { RowPropertyName } from './readGridToolbar/components/toggleColumns/types'
import { selectionDataToCopy } from './utils'
import { i18n } from '@lingui/core'

import 'react-contexify/dist/ReactContexify.min.css'
import { t, plural } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  verticalAlignMiddle: {
    verticalAlign: 'middle',
  },
  value: {
    fontFamily: 'monospace',
    fontWeight: 'bold',
  },
}))

const itemLabel = count =>
  plural(count, {
    one: 'Copy cell value',
    other: 'Copy cell values',
  })

export function ContextMenu({
  menuId,
  column = {},
  cellData = {},
  onShowCellHistory,
  readGridParent,
}) {
  const datasetQueryContext = useDatasetQueryContext()
  const { datasetDataId } = useDatasetDataContext()
  const dataset = useSelector(state => state.Datasets.datasetMetadataById[datasetDataId])
  const { getDataForSelectionCallback, selectedCells } = useContext(ReadGridContextMenuContext)
  const hasDatasetQueryContext = Boolean(datasetQueryContext)
  const hasDatasetContext = Boolean(dataset)
  const canSeeDatasetQueryOptions = useSelector(
    state => state.Auth.features['dataset-query-options'],
  )
  const cellHistoryEnabled = useSelector(state => state.Auth.features['dataset-versions-ui'])
  const dbColumnName = rowPropertyAliases.get(column?.rowPropertyName)?.db || column?.fieldId
  const operatorConfigGroups = useMemo(() => {
    const columnType = getColumnDataType(column?.governanceType, dbColumnName)
    const comparisonOperatorConfigs = comparisonOperatorConfigsByColType[columnType]
    const filteredOperatorConfigs = Object.values(comparisonOperatorConfigs).filter(
      operatorConfig => operatorConfig.supportedInQuickFilters(),
    )
    return Object.values(groupBy(filteredOperatorConfigs, 'group'))
  }, [dbColumnName])

  const scrollToTop = () => {
    // Only scroll rows; don't scroll columns
    readGridParent._bottomLeftGrid?.scrollToCell({ rowIndex: 0 })
    readGridParent._bottomRightGrid?.scrollToCell({ rowIndex: 0 })
  }

  const onShowCellHistoryMenuClick = ({ event, props }) => {
    onShowCellHistory && onShowCellHistory(event, props)
  }

  const onQuickFilterClick = data => {
    const { operatorConfig } = data.props

    const filter = new QueryOptionFilter(
      dbColumnName,
      operatorConfig.operator,
      operatorConfig.canHaveValueOperand() && !operatorConfig.data ? cellData.value : '',
    )
    datasetQueryContext.appendFilter(filter)
    scrollToTop()
  }

  const classes = useStyles()

  // You can right click on a cell without making a selection on that cell,
  // which is why the cellData.value is set initially;
  // additionally handle multiple cells being selected.
  const selectedCellsKeys = Object.keys(selectedCells || {})
  const hasSelectedMultipleCells = selectedCellsKeys.length > 1
  let cellValue = cellData?.value

  // If multiple cells are selected, set a value of that selection
  // instead of just a single cell.
  if (hasSelectedMultipleCells) {
    const selectionData = getDataForSelectionCallback(selectedCells)
    const selectionText = selectionDataToCopy({ data: selectionData })
    cellValue = selectionText
  }

  if (cellValue?.name) {
    cellValue = cellValue.name
  }

  const isSubmissionNameColumn = column?.rowPropertyName === RowPropertyName.submission
  if (isSubmissionNameColumn) cellValue = cellValue?.submission_name

  const formatSupportToExcel = { format: 'text/plain' }
  const isRowProperty = Boolean(column?.rowPropertyName)
  const showQuickFilter =
    canSeeDatasetQueryOptions &&
    hasDatasetContext &&
    hasDatasetQueryContext &&
    column?.rowPropertyName !== RowPropertyName.submission &&
    !column?.isClassificationColumn

  const showCellHistory = cellHistoryEnabled && hasDatasetContext && !isRowProperty

  const filteredOperatorConfigGroups = operatorConfigGroups
    .map(configGroup =>
      configGroup.filter(config => {
        if (config.needsValue && !cellValue) return false

        return true
      }),
    )
    .filter(group => group?.length)

  return (
    <Menu id={menuId}>
      <CopyToClipboard text={cellValue} options={formatSupportToExcel}>
        <Item>{itemLabel(selectedCellsKeys.length)}</Item>
      </CopyToClipboard>

      {/* filtering options if we have all the pieces we need */}
      {showQuickFilter && (
        <Submenu label={t`Quick filter`}>
          {filteredOperatorConfigGroups.map((operatorConfigGroup, index) => [
            operatorConfigGroup.map(operatorConfig => (
              <Item
                key={operatorConfig.operator}
                onClick={onQuickFilterClick}
                data={{ operatorConfig }}
              >
                <div>
                  <span>{` ${i18n._(operatorConfig.description)}`}</span>
                  {operatorConfig.canHaveValueOperand() && !operatorConfig.data && (
                    <span className={classes.value}>{` '${cellValue}'`}</span>
                  )}
                </div>
              </Item>
            )),
            index !== filteredOperatorConfigGroups.length - 1 && <Separator />,
          ])}
        </Submenu>
      )}

      {/* cell history option if the feature is enabled */}
      {showCellHistory && (
        <>
          <Separator />
          <Item onClick={onShowCellHistoryMenuClick}>
            <div style={{}}>
              <span className={classes.verticalAlignMiddle}>{t`Show cell history`}</span>
              <span className={classes.verticalAlignMiddle}>
                <Chip
                  label={t`New`}
                  variant="outlined"
                  color="primary"
                  size="small"
                  style={{ marginLeft: 18 }}
                />
              </span>
            </div>
          </Item>
        </>
      )}
    </Menu>
  )
}
